import { Editor, EditorArguments, Column, EditorValidationResult } from 'angular-slickgrid'; 

export class CustomDateTimeEditor implements Editor {
  private args!: EditorArguments;
  private container!: HTMLElement;
  private dateInput!: HTMLInputElement;
  private timeInput!: HTMLInputElement;
  private originalValue: string = '';
  private originalDatePart = '';
    private originalTimePart = '';

  constructor(args?: EditorArguments) {
    if (args) {
      this.init(args);
    }
  }

  init(args: EditorArguments) {
    this.args = args;
    this.container = args.container as HTMLElement;

    this.dateInput = document.createElement('input');
    this.dateInput.type = 'date';
    this.dateInput.className = 'form-control editor-date';
    this.dateInput.style.width = '65%';
    this.dateInput.style.display = 'inline-block';
    this.dateInput.style.boxSizing = 'border-box';
    this.dateInput.addEventListener('mousedown', e => e.stopPropagation());
    this.dateInput.addEventListener('click', e => e.stopPropagation());

    this.timeInput = document.createElement('input');
    this.timeInput.type = 'time';
    this.timeInput.className = 'form-control editor-time';
    this.timeInput.style.width = '32%';
    this.timeInput.style.display = 'inline-block';
    this.timeInput.style.marginLeft = '4%';
    this.timeInput.style.boxSizing = 'border-box';
    this.timeInput.addEventListener('mousedown', e => e.stopPropagation());
    this.timeInput.addEventListener('click', e => e.stopPropagation());

    this.container.appendChild(this.dateInput);
    this.container.appendChild(this.timeInput);
  }

  destroy(): void {
    if (this.dateInput && this.dateInput.parentElement) this.dateInput.remove();
    if (this.timeInput && this.timeInput.parentElement) this.timeInput.remove();
  }

  focus(): void {
    if (this.dateInput) this.dateInput.focus();
  }

  loadValue(item: any): void {
    const column = this.args.column as Column;
    const fieldName = column.field || '';
    const value = item?.[fieldName];

    this.originalValue = value ?? null;
    this.originalDatePart = '';
    this.originalTimePart = '';

    const pad2 = (n: number) => String(n).padStart(2, '0');

    if (typeof value === 'string' && value.includes('T')) {
      const normalized = value.replace(' ', 'T').split('.')[0];
      const [datePart, timePartRaw] = normalized.split('T');

      this.originalDatePart = datePart || '';
      this.originalTimePart = (timePartRaw || '').substring(0, 5) || '';

      if (/Z$|[+-]\d{2}:\d{2}$/.test(value)) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          const yyyy = d.getFullYear();
          const mm = pad2(d.getMonth() + 1);
          const dd = pad2(d.getDate());
          const hh = pad2(d.getHours());
          const mi = pad2(d.getMinutes());
          this.dateInput.value = `${yyyy}-${mm}-${dd}`;
          this.timeInput.value = `${hh}:${mi}`;
          return;
        }
      }

      this.dateInput.value = this.originalDatePart || '';
      this.timeInput.value = this.originalTimePart || '';
      return;
    }

    if (typeof value === 'string' && (/^\d{1,2}:\d{2}(:\d{2})?$/.test(value))) {
      const timeOnly = value.substring(0,5);
      this.originalTimePart = timeOnly;
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = pad2(today.getMonth() + 1);
      const dd = pad2(today.getDate());
      this.dateInput.value = `${yyyy}-${mm}-${dd}`;
      this.timeInput.value = timeOnly;
      return;
    }
  }

  serializeValue(): any {
    const datePart = this.dateInput.value || this.originalDatePart;
    const timePart = this.timeInput.value || this.originalTimePart || '00:00';

    if (!datePart) return null;

    const [y, m, d] = datePart.split('-').map(n => parseInt(n, 10));
    const [hh, mm] = timePart.split(':').map(n => parseInt(n, 10));
    const local = new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);

    return local.toISOString();
    }   

  applyValue(item: any, state: any): void {
    const column = this.args.column as Column;
    const fieldName = column.field || '';
    item[fieldName] = state;
  }

  isValueChanged(): boolean {
    const current = this.serializeValue();
    return String(current) !== String(this.originalValue);
  }

  validate(): EditorValidationResult {
    const column = this.args.column as Column;
    const editorOptions = (column.editor as any) || {};
    const dateVal = this.dateInput.value;
    const timeVal = this.timeInput.value;

    if (editorOptions?.required && !dateVal) {
      return { valid: false, msg: 'Date is required' };
    }

    if (timeVal && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeVal)) {
      return { valid: false, msg: 'Invalid time format' };
    }

    if (editorOptions?.validator) {
      const item = this.args.item || {};
      const simulated = { ...item };
      const columnField = (this.args.column as Column).field;
      simulated[columnField] = this.serializeValue();
      const validationArgs = { ...this.args, item: simulated };
      return editorOptions.validator(simulated[columnField], validationArgs);
    }

    return { valid: true, msg: '' };
  }
}